import React, { useMemo } from 'react'
import { TextField, FormControl, FormHelperText, Tooltip } from '@mui/material'
import { Icon, Typography, MenuItem, Box } from '@mui/material'

const ROWS_MAX = 5
const DEFAULT_MAX_LENGTH = 250

/**
 * @typedef {object} SelectOption
 * @property {string | number} key - El identificador único de la opción.
 * @property {string} value - El texto que se mostrará para la opción.
 */

/**
 * @typedef {object} TextInputProps
 * @property {'text' | 'email' | 'password' | 'number' | 'tel' | 'url'} [type='text'] - El tipo de input HTML.
 * @property {boolean} [readOnly=false] - Si es `true`, muestra el valor como texto estático.
 * @property {number} [rowsMax=5] - El número máximo de filas para campos multilínea.
 * @property {string} [title] - El título que se muestra sobre el campo.
 * @property {string} [tooltip] - Un texto de ayuda que se muestra en un tooltip junto al título.
 * @property {'small' | 'normal'} [fontSize] - Controla el tamaño de la fuente del título.
 * @property {string} [value] - El valor actual del campo.
 * @property {string} [placeholder] - Texto de marcador de posición para el campo.
 * @property {function(React.ChangeEvent): void} onChange - La función callback que se ejecuta cuando el valor cambia.
 * @property {boolean} [multiline] - Si es `true`, el campo será multilínea (textarea).
 * @property {string} [url] - Si se proporciona y `readOnly` es `true`, el valor se renderiza como un enlace.
 * @property {string} [units] - Unidades a mostrar después del valor (ej. 'kg', 'm', '€').
 * @property {boolean} [error] - Si es `true`, el campo se muestra con estilo de error.
 * @property {string} [errorMessage] - El mensaje de error a mostrar cuando `error` es `true`.
 * @property {string} [helpMessage] - Un mensaje de ayuda a mostrar cuando no hay error.
 * @property {SelectOption[]} [values] - Si se proporciona, el campo se convierte en un select con estas opciones.
 * @property {object} [InputProps] - Props adicionales a pasar al componente `TextField`.
 * @property {object} [inputProps] - Props adicionales a pasar al elemento input nativo.
 * @property {boolean} [required] - Si es `true`, el campo se marca como requerido y valida que no esté vacío.
 */

/**
 * Un componente de entrada de texto versátil y altamente configurable.
 * 
 * Soporta múltiples modos: texto simple, multilínea, select, solo lectura con enlace opcional,
 * y validación con mensajes de error. Incluye soporte para tooltip, unidades, y límite de caracteres.
 * En modo lectura, puede renderizar el valor como enlace si se proporciona una URL.
 * 
 * @component
 * @example
 * const MyForm = () => {
 *   const [name, setName] = useState('');
 *   const [error, setError] = useState(false);
 * 
 *   const handleChange = (event) => {
 *     setName(event.target.value);
 *     setError(event.target.value.length < 3);
 *   };
 * 
 *   return (
 *     <TextInput
 *       title="Nombre completo"
 *       tooltip="Ingrese su nombre y apellidos"
 *       value={name}
 *       onChange={handleChange}
 *       error={error}
 *       errorMessage="El nombre debe tener al menos 3 caracteres"
 *       required
 *     />
 *   );
 * };
 * 
 * @example
 * // Campo multilínea
 * <TextInput
 *   title="Comentarios"
 *   value={comments}
 *   onChange={handleChange}
 *   multiline
 *   rowsMax={10}
 * />
 * 
 * @example
 * // Modo lectura con enlace
 * <TextInput
 *   title="Sitio web"
 *   value="www.ejemplo.com"
 *   url="https://www.ejemplo.com"
 *   readOnly
 * />
 * 
 * @param {TextInputProps} props - Las props del componente TextInput.
 */
export const TextInput = React.memo(
  ({ type = 'text', readOnly = false, rowsMax = ROWS_MAX, ...props }) => {
    const inputSx = {
      height: props.multiline ? 'auto' : '48px',
    }

    const InputProps = useMemo(() => {
      return {
        ...props.InputProps,
        sx: { ...inputSx, ...props.InputProps?.sx },
      }
    }, [props.InputProps, props.multiline])

    const tooltip = useMemo(
      () => (
        <Box component="span" sx={{ fontSize: 16, ml: 0.5 }}>
          <Tooltip
            placement="top"
            title={
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {props.tooltip}
              </Typography>
            }
          >
            <Icon fontSize="inherit">help_outline</Icon>
          </Tooltip>
        </Box>
      ),
      [props.tooltip]
    )

    const inputPropsDefault = {
      pattern: props.required ? '.*[^ ].*' : null,
      maxLength: DEFAULT_MAX_LENGTH,
    }

    const typographyVariant = props.fontSize === 'small' ? '2' : '1'

    return (
      <FormControl sx={{ width: '100%' }} margin="none">
        <Box>
          {props.title && (
            <Typography
              display="inline"
              variant={`subtitle${typographyVariant}`}
              sx={{
                marginBottom: '8px !important',
                fontFamily: 'FSEmeric-Medium',
              }}
            >
              {props.title}
            </Typography>
          )}
          {props.tooltip ? tooltip : null}
        </Box>
        {readOnly ? (
          <Typography
            variant={`body${typographyVariant}`}
            color="text.secondary"
            sx={{ whiteSpace: 'pre-line', wordBreak: 'break-all' }}
          >
            {props.url && props.value ? (
              <Box
                component="a"
                href={props.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: '#004a6b', textDecoration: 'none' }}
              >
                {props.value ? props.value + (props.units ? ' ' + props.units : '') : '--'}
              </Box>
            ) : props.value ? (
              props.value + (props.units ? ' ' + props.units : '')
            ) : (
              '--'
            )}
          </Typography>
        ) : (
          <>
            <TextField
              maxRows={rowsMax}
              {...props}
              type={type}
              variant="outlined"
              value={props.value || ''}
              placeholder={props.placeholder}
              onChange={props.onChange}
              InputProps={InputProps}
              inputProps={{ ...inputPropsDefault, ...props.inputProps }}
            >
              {props.values
                ? props.values.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                      {option.value}
                    </MenuItem>
                  ))
                : null}
            </TextField>
            {(props.errorMessage || props.helpMessage) && (
              <FormHelperText id="my-helper-text" error={props.error}>
                {props.error ? props.errorMessage : props.helpMessage}
              </FormHelperText>
            )}
          </>
        )}
      </FormControl>
    )
  }
)
